/**
 * 服務訂閱與配額（Sprint 4-pre · 租戶管理員 · own-org）
 * func_code: ee_subscription（is_mana=FALSE · migration 078 seed · route path===func_code）
 * 申請訂閱 + 本租戶訂閱清單 + 使用者配額管控（override）+ 用量總覽。
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Select, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useMessage } from '../contexts/MessageContext';
import { usePermission } from '../hooks/usePermission';
import {
  requestSubscription, listMine, listUserQuotas, setUserOverride, tenantUserUsage,
  listAvailableServices, resolveServiceLabel, blockUserService, unblockUserService,
  Subscription, UserQuotaRow, TenantUsage, ServiceCode,
} from '../services/subscriptionService';

const FUNC_CODE = 'ee_subscription';

const statusChip = (s: string) => {
  const map: Record<string, 'default' | 'success' | 'error' | 'warning'> = {
    pending: 'warning', approved: 'success', rejected: 'error', suspended: 'default', expired: 'default',
  };
  return <Chip size="small" color={map[s] || 'default'} label={s} />;
};

const TenantSubscriptionPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const tt = (k: string, d: string) => t(`subscription.${k}`, d);
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission, loading: permLoading } = usePermission();
  const canRead = hasPermission(FUNC_CODE, 'read');
  const canCreate = hasPermission(FUNC_CODE, 'create');
  const canUpdate = hasPermission(FUNC_CODE, 'update');

  const [services, setServices] = useState<ServiceCode[]>([]);
  const [mine, setMine] = useState<Subscription[]>([]);
  const [reqService, setReqService] = useState('');
  const [quotaService, setQuotaService] = useState('');
  const [quotas, setQuotas] = useState<UserQuotaRow[]>([]);
  const [usage, setUsage] = useState<TenantUsage | null>(null);
  const [overrideInput, setOverrideInput] = useState<Record<number, string>>({});
  const [blockFor, setBlockFor] = useState<UserQuotaRow | null>(null);   // D17 停用對話框
  const [blockReason, setBlockReason] = useState('');
  const label = (code: string) => resolveServiceLabel(code, services, i18n.language);

  // 可申請服務 registry（system_codes）→ 設定 dropdown 預設
  useEffect(() => {
    listAvailableServices().then((svcs) => {
      setServices(svcs);
      if (svcs.length) { setReqService(svcs[0].code); setQuotaService(svcs[0].code); }
    }).catch(() => { /* silent ok: 無 registry → 空 dropdown + hint */ });
  }, []);

  const loadMine = useCallback(async () => {
    try { setMine(await listMine()); } catch (err) { showApiError(err); }
  }, [showApiError]);

  const loadQuotas = useCallback(async (svc: string) => {
    try {
      const [q, u] = await Promise.all([listUserQuotas(svc), tenantUserUsage()]);
      setQuotas(q); setUsage(u);
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { if (!permLoading && canRead) loadMine(); }, [permLoading, canRead, loadMine]);
  useEffect(() => { if (!permLoading && canRead && quotaService) loadQuotas(quotaService); }, [permLoading, canRead, quotaService, loadQuotas]);

  const handleRequest = async () => {
    try {
      await requestSubscription(reqService);
      showSuccess('SYS390001', { name: reqService });
      await loadMine();
    } catch (err) { showApiError(err); }
  };

  const handleOverride = async (row: UserQuotaRow) => {
    const raw = overrideInput[row.user_id];
    const val = raw === undefined || raw === '' ? null : parseInt(raw, 10);
    if (val !== null && (Number.isNaN(val) || val < 0)) return;
    try {
      await setUserOverride(row.user_id, quotaService, val);
      showSuccess('SYS020002', { name: `${row.username} 配額` });
      await loadQuotas(quotaService);
    } catch (err) { showApiError(err); }
  };

  const confirmBlock = async () => {
    if (!blockFor) return;
    try {
      await blockUserService(blockFor.user_id, quotaService, blockReason.trim() || undefined);
      showSuccess('SYS390021');
      setBlockFor(null); setBlockReason('');
      await loadQuotas(quotaService);
    } catch (err) { showApiError(err); }
  };
  const handleUnblock = async (row: UserQuotaRow) => {
    try {
      await unblockUserService(row.user_id, quotaService);
      showSuccess('SYS390021');
      await loadQuotas(quotaService);
    } catch (err) { showApiError(err); }
  };

  if (!permLoading && !canRead) {
    return <div className="page-container"><div className="error-message">{t('common.noPermission', '無此功能權限')}</div></div>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>💳 {tt('tenantTitle', '服務訂閱與配額')}</Typography>

      {/* 申請訂閱 */}
      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>{tt('requestTitle', '申請訂閱服務')}</Typography>
        {services.length === 0 ? (
          <Alert severity="info">{tt('noAvailableServices', '目前無可申請訂閱的服務，敬請期待')}</Alert>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <Select size="small" value={reqService} onChange={(e) => setReqService(e.target.value)}>
              {services.map((s) => <MenuItem key={s.code} value={s.code}>{label(s.code)}</MenuItem>)}
            </Select>
            <Button variant="contained" disabled={!canCreate} onClick={handleRequest}>{tt('request', '申請')}</Button>
          </Stack>
        )}
        <Box sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>{tt('service', '服務')}</TableCell>
              <TableCell>{tt('status', '狀態')}</TableCell>
              <TableCell>{tt('defaultQuota', '預設配額/人')}</TableCell>
              <TableCell>{tt('rejectReason', '拒絕原因')}</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {mine.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{label(s.service_code)}
                    <Typography variant="caption" color="text.secondary"> ({s.service_code})</Typography>
                  </TableCell>
                  <TableCell>{statusChip(s.status)}</TableCell>
                  <TableCell>{String((s.quota_config as { default_per_user?: number })?.default_per_user ?? '—')}</TableCell>
                  <TableCell>{s.rejection_reason || '—'}</TableCell>
                </TableRow>
              ))}
              {mine.length === 0 && (
                <TableRow><TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">{tt('noSub', '尚無訂閱')}</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Card>

      {/* 使用者配額管控 */}
      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">{tt('quotaTitle', '使用者配額管控')}</Typography>
          <Select size="small" value={quotaService} onChange={(e) => setQuotaService(e.target.value)}>
            {services.map((s) => <MenuItem key={s.code} value={s.code}>{label(s.code)}</MenuItem>)}
          </Select>
        </Stack>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>{tt('user', '使用者')}</TableCell>
            <TableCell>{tt('baseQuota', '預設配額')}</TableCell>
            <TableCell>{tt('override', '個別覆寫')}</TableCell>
            <TableCell>{tt('used', '已用 tokens')}</TableCell>
            <TableCell>{tt('status', '狀態')}</TableCell>
            <TableCell>{tt('actions', '操作')}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {quotas.map((row) => (
              <TableRow key={row.user_id}>
                <TableCell>{row.username} ({row.account})</TableCell>
                <TableCell>{row.base_quota_tokens.toLocaleString()}</TableCell>
                <TableCell>
                  <TextField size="small" type="number" sx={{ width: 120 }}
                    placeholder={String(row.override_quota ?? '')}
                    value={overrideInput[row.user_id] ?? (row.override_quota ?? '').toString()}
                    onChange={(e) => setOverrideInput((p) => ({ ...p, [row.user_id]: e.target.value }))} />
                </TableCell>
                <TableCell>{row.used_tokens.toLocaleString()} / {row.effective_limit ? row.effective_limit.toLocaleString() : '∞'}</TableCell>
                <TableCell>
                  {row.blocked
                    ? <Chip size="small" color="error" label={row.block_reason
                        ? `${tt('blocked', '已停用')}：${row.block_reason}` : tt('blocked', '已停用')} />
                    : <Chip size="small" color="success" label={tt('active', '啟用')} />}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" disabled={!canUpdate} onClick={() => handleOverride(row)}>{tt('save', '儲存')}</Button>
                    {row.blocked
                      ? <Button size="small" color="success" disabled={!canUpdate}
                          onClick={() => handleUnblock(row)}>{tt('unblock', '取消停用')}</Button>
                      : <Button size="small" color="error" disabled={!canUpdate}
                          onClick={() => { setBlockFor(row); setBlockReason(''); }}>{tt('block', '停用')}</Button>}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* D17 停用對話框（原因選填）*/}
      <Dialog open={Boolean(blockFor)} onClose={() => setBlockFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>{tt('blockTitle', '停用使用者 AI 服務')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {tt('blockHint', '停用後此使用者無法使用此 AI 服務（跨月持續，直到取消停用）。')}
          </Typography>
          <TextField fullWidth size="small" autoFocus label={tt('blockReason', '原因（選填）')}
            value={blockReason} onChange={(e) => setBlockReason(e.target.value)} inputProps={{ maxLength: 200 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockFor(null)}>{t('common.cancel', '取消')}</Button>
          <Button color="error" variant="contained" onClick={confirmBlock}>{tt('block', '停用')}</Button>
        </DialogActions>
      </Dialog>

      {/* 用量總覽 */}
      <Card variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>{tt('usageTitle', '租戶 AI 用量')}（{usage?.month || '—'}）</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {tt('totalTokens', '本月總用量')}: <strong>{(usage?.total_tokens ?? 0).toLocaleString()}</strong> tokens
        </Typography>
        <Typography variant="caption" color="text.secondary">{tt('topUsers', '用量前 5 名')}</Typography>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          {(usage?.top_users ?? []).map((u, i) => (
            <Typography key={`${u.user_id}-${u.service_code}`} variant="body2">
              {i + 1}. {u.username} · {u.service_code} · {u.used_tokens.toLocaleString()} tokens
            </Typography>
          ))}
          {(usage?.top_users ?? []).length === 0 && (
            <Typography variant="body2" color="text.secondary">{tt('noUsage', '本月尚無用量')}</Typography>
          )}
        </Stack>
      </Card>
    </Box>
  );
};

export default TenantSubscriptionPage;
